import * as React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { CartItem } from '../plan-selection-types';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CartTileProps {
  item: CartItem;
  isPrimary: boolean;
  onRemove: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CartTile = ({ item, isPrimary, onRemove }: CartTileProps) => (
  <Box sx={{
    position: 'relative',
    border: '1px solid #e5e7eb', borderRadius: 1, p: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    transition: 'box-shadow 150ms',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  }}>
    {/* Remove button (non-primary only) */}
    {!isPrimary && (
      <IconButton
        onClick={onRemove}
        size="small"
        sx={{
          position: 'absolute', top: -10, right: -10,
          bgcolor: '#f9fafb', border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          width: 26, height: 26,
          color: '#9ca3af',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
        }}
      >
        <X size={12} />
      </IconButton>
    )}

    {/* Left: logo + info */}
    <Stack direction={{sm: "row"}} alignItems="flex-start" gap={{xs: 1, sm: 2}}>
      {item.logoUrl && (
        <Box
          component="img"
          src={item.logoUrl}
          alt="carrier logo"
          onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
          sx={{
            height: { xs: 24, sm: 48 },
            width: { xs: 'auto', sm: 48 },
            objectFit: 'contain',
            mt: 0.25,
            flexShrink: 0,
          }}
        />
      )}
      <Stack gap={0.25}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#101828', lineHeight: 1.3 }}>
          {item.planName}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
          Deductible: {item.deductible || '$0'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
          Max Out-of-Pocket: {item.moop || 'NA'}
        </Typography>
      </Stack>
    </Stack>

    {/* Right: premium */}
    <Box sx={{
      textAlign: 'right', flexShrink: 0, pl: 2,
      borderLeft: '1px solid #e5e7eb', ml: 2,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#009689', lineHeight: 1.1 }}>
        ${item.premium.toFixed(2)}
      </Typography>
      <Typography sx={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Monthly
      </Typography>
    </Box>
  </Box>
);
